import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { ConfirmProvider, useConfirm } from '../components/ConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Créer une instance axios avec intercepteur pour toujours avoir le bon token
  const api = React.useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
    });
    
    // Intercepteur pour ajouter le token à chaque requête
    instance.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Intercepteur pour gérer les erreurs 401
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token invalide ou expiré - déconnecter
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        setLoading(false);
        return;
      }
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      // Ne pas déconnecter immédiatement, l'intercepteur gère les 401
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, tenantSlug = null) => {
    const response = await api.post('/auth/login', { email, password, tenantSlug });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { token: newToken, user: newUser, requiresVerification } = response.data;
    
    // Si le compte nécessite une validation email, ne pas connecter l'utilisateur
    if (requiresVerification) {
      return { user: newUser, requiresVerification: true };
    }
    
    // Sinon (admin), connecter directement
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
    return { user: newUser, requiresVerification: false };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (updates) => {
    const response = await api.put('/auth/me', updates);
    setUser(response.data);
    return response.data;
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateProfile, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Tenant Context
const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used within TenantProvider');
  return context;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTenant = useCallback(async (slug) => {
    if (!slug) {
      setTenant(null);
      return null;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/artists/public/${slug}`);
      setTenant(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant:', error);
      setTenant(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearTenant = () => setTenant(null);

  // Apply tenant colors as CSS variables
  useEffect(() => {
    if (tenant?.artist) {
      const root = document.documentElement;
      const primaryColor = tenant.artist.primaryColor || '#D4AF37';
      const secondaryColor = tenant.artist.secondaryColor || '#1A1A1A';
      
      // Convert hex to HSL for CSS variables
      root.style.setProperty('--tenant-primary', primaryColor);
      root.style.setProperty('--tenant-secondary', secondaryColor);
    }
  }, [tenant]);

  return (
    <TenantContext.Provider value={{ tenant, loading, fetchTenant, clearTenant }}>
      {children}
    </TenantContext.Provider>
  );
};

// Combined Provider
export const AppProviders = ({ children }) => (
  <LanguageProvider>
    <ThemeProvider>
      <AuthProvider>
        <TenantProvider>
          <ConfirmProvider>
            {children}
          </ConfirmProvider>
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  </LanguageProvider>
);

// Re-export useLanguage and useConfirm for convenience
export { useLanguage, useConfirm };

export default AppProviders;
